/* ═══════════════════════════════════════════════════════════════
   CSI SECTIONFORMAT DEFAULTS — one source of truth.

   docs/DESIGNER_UNIFIED_PLAN.md §3.1 (Josh, 2026-08-12). The
   published CSI SectionFormat / PageFormat conventions for every
   level's spacing, indent and hanging indent, in ONE place, used
   three ways:

     (a) SEED — a brand-new forma starts CSI-correct instead of
         starting blank and being hand-tuned into approximate
         correctness;
     (b) GAP-FILL — an import fills only what the source document
         did not say. **Imported values always win where present.**
         A harvested template is evidence; a default is a guess;
     (c) RESET — a per-level "back to the CSI default" affordance,
         and the answer to "is this value mine or the standard's?".

   Nothing here is a preference. These are the conventions the
   architect's own office standard is measured against, which is why
   defaults are LABELLED as defaults wherever they show (§3.1: "
   Defaults are visibly labeled as defaults").

   Units match the forma model exactly: indents in INCHES, spacing in
   POINTS. No conversion happens here, so a value read out of this
   table can be compared to a level's value directly.

   ── The conventions, and why the numbers are what they are ──

   SectionFormat's page schema is a 0.5" indent ladder: PART headings
   sit at the left margin, and each subordinate level steps in half an
   inch. The number/letter sits at the level's indent and the text
   starts half an inch further right — that is the hanging indent, and
   it is what makes a spec's numbers line up in a column instead of
   drifting with the text.

     PART 1 - GENERAL          level 1   flush left
       1.1   ARTICLE           level 2   0.0"  (number in the margin)
         A.  Paragraph         level 3   0.5"
           1. Subparagraph     level 4   1.0"
             a.                level 5   1.5"
               1)              level 6   2.0"
                 a)            level 7   2.5"
                   (1)         level 8   3.0"
                     (a)       level 9   3.5"

   Vertical rhythm: a blank line (12pt at a 12pt body) above each new
   heading level, none below it, so the heading binds to the text it
   introduces. PARTs get a full line above and below; the deepest
   levels get none at all — nested lists read as a block.
   ═══════════════════════════════════════════════════════════════ */

import type { LevelStyle, StandardConfig } from './forma';

/** The CSI-defined shape of one level. A subset of LevelStyle: only the
 *  properties SectionFormat actually specifies. Everything else (colour,
 *  keep-with-next, justification) is the firm's, not the standard's. */
export interface CsiLevelDefault {
  level: number;
  /** How the level is written in SectionFormat, for the UI's own labels. */
  label: string;
  /** Distance from the left margin to the number, in INCHES. */
  indent: number;
  /** Distance from the number to the text, in INCHES. */
  hangingIndent: number;
  /** Space above, in POINTS. */
  spaceBefore: number;
  /** Space below, in POINTS. */
  spaceAfter: number;
  /** PART headings are the only level SectionFormat prints bold+caps. */
  bold: boolean;
  allCaps: boolean;
}

/** The published conventions, levels 1–9. Index 0 = level 1. */
export const CSI_LEVEL_DEFAULTS: readonly CsiLevelDefault[] = Object.freeze([
  {
    level: 1,
    label: 'PART 1 - GENERAL',
    indent: 0,
    hangingIndent: 0,
    spaceBefore: 12,
    spaceAfter: 12,
    bold: true,
    allCaps: true,
  },
  {
    level: 2,
    label: '1.1  ARTICLE',
    indent: 0,
    hangingIndent: 0.5,
    spaceBefore: 12,
    spaceAfter: 6,
    bold: false,
    allCaps: true,
  },
  {
    level: 3,
    label: 'A.  Paragraph',
    indent: 0.5,
    hangingIndent: 0.5,
    spaceBefore: 6,
    spaceAfter: 0,
    bold: false,
    allCaps: false,
  },
  {
    level: 4,
    label: '1.  Subparagraph',
    indent: 1,
    hangingIndent: 0.5,
    spaceBefore: 0,
    spaceAfter: 0,
    bold: false,
    allCaps: false,
  },
  {
    level: 5,
    label: 'a.',
    indent: 1.5,
    hangingIndent: 0.5,
    spaceBefore: 0,
    spaceAfter: 0,
    bold: false,
    allCaps: false,
  },
  {
    level: 6,
    label: '1)',
    indent: 2,
    hangingIndent: 0.5,
    spaceBefore: 0,
    spaceAfter: 0,
    bold: false,
    allCaps: false,
  },
  {
    level: 7,
    label: 'a)',
    indent: 2.5,
    hangingIndent: 0.5,
    spaceBefore: 0,
    spaceAfter: 0,
    bold: false,
    allCaps: false,
  },
  {
    level: 8,
    label: '(1)',
    indent: 3,
    hangingIndent: 0.5,
    spaceBefore: 0,
    spaceAfter: 0,
    bold: false,
    allCaps: false,
  },
  {
    level: 9,
    label: '(a)',
    indent: 3.5,
    hangingIndent: 0.5,
    spaceBefore: 0,
    spaceAfter: 0,
    bold: false,
    allCaps: false,
  },
]);

/** The CSI default for a level number (1-based), or undefined past level 9. */
export function csiDefaultFor(level: number): CsiLevelDefault | undefined {
  return CSI_LEVEL_DEFAULTS.find((d) => d.level === level);
}

/** The properties of a level that CSI actually specifies. */
export type CsiGoverned = 'indent' | 'hangingIndent' | 'spaceBefore' | 'spaceAfter';

const CSI_GOVERNED: readonly CsiGoverned[] = [
  'indent',
  'hangingIndent',
  'spaceBefore',
  'spaceAfter',
];

/** A fully-specified level style at the CSI default, ready to seed with. */
export function csiLevelStyle(level: number): LevelStyle {
  const d = csiDefaultFor(level);
  return {
    level,
    bold: d?.bold ?? false,
    allCaps: d?.allCaps ?? false,
    italic: false,
    underline: false,
    fontColor: '#000000',
    indent: d?.indent ?? 0,
    hangingIndent: d?.hangingIndent ?? 0.5,
    spaceBefore: d?.spaceBefore ?? 0,
    spaceAfter: d?.spaceAfter ?? 0,
    keepWithNext: level <= 2,
    keepLinesTogether: false,
    noSpaceSameStyle: false,
  };
}

/** Nine CSI-correct levels — what a FRESH forma starts from (use (a)). */
export function csiLevelStyles(): LevelStyle[] {
  return CSI_LEVEL_DEFAULTS.map((d) => csiLevelStyle(d.level));
}

/**
 * Use (b) — GAP-FILL. Return `level` with every CSI-governed property that
 * the source left unsaid filled in from the standard.
 *
 * "Unsaid" means undefined or null — NOT zero. A template that genuinely
 * specifies a 0" indent is making a statement, and overwriting it with 0.5"
 * would silently reformat an architect's own standard. That distinction is
 * the whole reason this function exists rather than a spread with defaults.
 */
export function fillCsiGaps<T extends Partial<LevelStyle> & { level: number }>(
  level: T
): T & Partial<Pick<LevelStyle, CsiGoverned>> & { csiFilled: CsiGoverned[] } {
  const d = csiDefaultFor(level.level);
  const filled: CsiGoverned[] = [];
  if (!d) return { ...level, csiFilled: filled };
  const out = { ...level } as T & Record<CsiGoverned, number>;
  for (const key of CSI_GOVERNED) {
    const v = (level as Partial<LevelStyle>)[key];
    if (v === undefined || v === null || Number.isNaN(v as number)) {
      out[key] = d[key];
      filled.push(key);
    }
  }
  return { ...out, csiFilled: filled };
}

/** Gap-fill an entire level list. Imported values always win where present. */
export function fillCsiGapsAll(
  levels: Array<Partial<LevelStyle> & { level: number }>
): LevelStyle[] {
  return levels.map((l) => {
    const { csiFilled: _csiFilled, ...rest } = fillCsiGaps(l);
    return rest as LevelStyle;
  });
}

/**
 * Use (c) — RESET. `level` with every CSI-governed property put back to the
 * standard. Everything the firm owns (colour, bold, keeps, justification) is
 * untouched: this resets the SPACING SCHEMA, not the house style.
 */
export function resetLevelToCsi(level: LevelStyle): LevelStyle {
  const d = csiDefaultFor(level.level);
  if (!d) return level;
  return {
    ...level,
    indent: d.indent,
    hangingIndent: d.hangingIndent,
    spaceBefore: d.spaceBefore,
    spaceAfter: d.spaceAfter,
  };
}

/** Which CSI-governed properties of this level currently match the standard —
 *  what the UI labels as "CSI default" rather than a firm decision (§3.1). */
export function csiMatches(
  level: Partial<LevelStyle> & { level: number }
): Record<CsiGoverned, boolean> {
  const d = csiDefaultFor(level.level);
  const out = {} as Record<CsiGoverned, boolean>;
  for (const key of CSI_GOVERNED) {
    out[key] = !!d && (level as Partial<LevelStyle>)[key] === d[key];
  }
  return out;
}

/** True when every CSI-governed property of this level is at the standard. */
export function isCsiDefault(level: Partial<LevelStyle> & { level: number }): boolean {
  return Object.values(csiMatches(level)).every(Boolean);
}

/**
 * Gap-fill a whole forma's levels in place (pure — returns a new config).
 * Applied AFTER an import has said everything it can, never before.
 */
export function fillFormaCsiGaps(config: StandardConfig): StandardConfig {
  if (!config.levels?.length) return { ...config, levels: csiLevelStyles() };
  return { ...config, levels: fillCsiGapsAll(config.levels) };
}
